import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-bootstrap/lib/Modal';
import Panel from 'react-bootstrap/lib/Panel';
import ModalHeader from '../ModalHeader.js';
import Icon from '../Icon';
import Button from '../Button';
import i18n from '../../i18n/translate';
import { filtersArrayToKeyValueObject, isFilterActive, getInitialFilters, clearAllFilters, removeFilterFromCollection } from './functions';
import noop from '../../util/noop';
import FilterChips from './FilterChips.js';
import DateFilter from './DateFilter.js';
import { kebabCase } from 'lodash';

const FilterModal = ({
  applyButtonText,
  backdropClassName,
  cancelButtonText,
  canClose,
  className,
  filterConfig,
  initialFilters,
  modalProps,
  onSelectFilters,
  onClearFilters = noop,
  onClose = noop,
  show,
  title
}) => {
  const [filters, setFilters] = useState([]);
  const [expandedFilters, setExpandedFilters] = useState([]);
  const activeFilters = filters.filter(filter => isFilterActive(filter));

  useEffect(() => {
    const filters = getInitialFilters(filterConfig, initialFilters);
    setFilters(filters);
  }, []);

  const handleApplyFilters = () => {
    onSelectFilters(filtersArrayToKeyValueObject(filters));
  }

  const clearFilters = () => {
    const newFilters = clearAllFilters(filters);
    setFilters(newFilters);
    onClearFilters();
  }

  const removeFilter = filterToRemove => {
    const newFilters = removeFilterFromCollection(filterToRemove, filters);
    setFilters(newFilters);
  }

  const onTogglePanel = (expanded, expandedFilter) => {
    if (expanded) {
      setExpandedFilters([...expandedFilters, expandedFilter]);
    } else {
      setExpandedFilters(expandedFilters.filter(filter => filter !== expandedFilter));
    }
  }

  const handleFilterChange = (updatedFilter, value) => {
    const newFilters = filters.map(filter => {
      if (filter.name === updatedFilter.name) {
        if(filter.type === DateFilter) {
          filter.value = { ...filter.value, ...value };
        } else {
          filter.value = value;
        }
      }

      return filter;
    });

    setFilters(newFilters);
  }

  const renderFooter = () => (
    <Modal.Footer className="sticky-footer">
      {activeFilters.length > 0 && (
        <div className="selected-filters">
          <p className="title">{i18n.t('Selected Filters')}</p>
          <FilterChips filters={activeFilters} onRemoveFilter={removeFilter} />
        </div>
      )}
      <div className="footer-buttons">
        {activeFilters.length > 0 && (
          <Button
            onClick={clearFilters}
            style="link"
            className="clear-filters"
            data-action="clear-filters"
            icon="close"
            label={i18n.t('Clear Filters')}
          />
        )}
        <Button style="secondary" onClick={onClose} data-action="cancel-filters">
          {cancelButtonText}
        </Button>
        <Button style="primary" onClick={handleApplyFilters} data-action="apply-filters">
          {applyButtonText}
        </Button>
      </div>
    </Modal.Footer>
  );

  const renderPanelHeading = filter => {
    const { name, label, value, getSelectionDisplay } = filter;
    const selectedCount = Array.isArray(value) ? value.length : null;
    const iconType = expandedFilters.includes(name) ? 'chevron-down' : 'chevron-right';
    const selectedText = getSelectionDisplay ? getSelectionDisplay(value) : `${selectedCount} ${i18n.t('selected')}`;

    return (
      <Panel.Toggle className="card-header" componentClass="div">
        <span className="card-title">{label}</span>
        <Icon iconType={iconType} additionalClasses="card-icon" />
        {(selectedCount !== null || !!getSelectionDisplay) && (
          <span className="card-selected">{selectedText}</span>
        )}
      </Panel.Toggle>
    );
  }

  const renderBody = () => (
    <Modal.Body>
      {filters.map(filter => {
        const FilterComponent = filter.type;

        return (
          <Panel
            key={`panel_${filter.name}`}
            className="filter-panel bordered"
            onToggle={expanded => onTogglePanel(expanded, filter.name)}
            data-action={`open-${kebabCase(filter.name)}-panel`}
          >
            <Panel.Body>
              {renderPanelHeading(filter)}
              <Panel.Collapse className="filter-value">
                <FilterComponent
                  {...filter}
                  key={filter.name}
                  name={filter.label}
                  onChange={(value, action) => handleFilterChange(filter, value, action)}
                  disabled={!!filter.disabled}
                  isWrapper={true}
                  clearable={false}
                />
              </Panel.Collapse>
            </Panel.Body>
          </Panel>
        );
      })}
    </Modal.Body>
  );

  return (
    <Modal
      id={modalProps.id}
      className={`filter-modal sticky-footer ${className || ''}`}
      bsSize={modalProps.bsSize}
      show={show}
      onHide={onClose}
      keyboard={canClose}
      backdrop={canClose ? true : 'static'}
      backdropClassName={backdropClassName || ''}

    >
      <ModalHeader onCancel={onClose} canClickOff={canClose} title={title} />
      {renderBody()}
      {renderFooter()}
    </Modal>
  );
}

FilterModal.propTypes = {
  applyButtonText: PropTypes.string,
  backdropClassName: PropTypes.string,
  cancelButtonText: PropTypes.string,
  canClose: PropTypes.bool,
  className: PropTypes.string,
  filterConfig: PropTypes.arrayOf(PropTypes.shape({
    disabled: PropTypes.bool,
    getChipDisplay: PropTypes.func,
    getSelectionDisplay: PropTypes.func,
    label: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    type: PropTypes.element.isRequired,
    value: PropTypes.oneOfType([PropTypes.array, PropTypes.object]).isRequired
  })),
  initialFilters: PropTypes.object,
  modalProps: PropTypes.shape({
    id: PropTypes.string,
    bsSize: PropTypes.oneOf(['sm', 'md', 'lg'])
  }),
  onClearFilters: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  onSelectFilters: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  show: PropTypes.bool
};

FilterModal.defaultProps = {
  applyButtonText: i18n.t('Apply Filters'),
  cancelButtonText: i18n.t('Cancel'),
  canClose: true,
  filterConfig: [],
  initialFilters: {},
  modalProps: {
    id: 'filterModal',
    bsSize: 'md'
  },
  show: true
};

export default FilterModal;
